// Export library items as BibTeX, RIS, or CSL-JSON.
// Pure helpers — no I/O. Caller decides whether to download or copy to clipboard.
//
// Scope is intentionally minimal: covers the seven source_types we ship today
// (book, journal_article, chapter, proceedings_article, website, report,
// podcast, other). For unmapped types we fall back to "misc"/"GEN"/"document"
// so the file is still importable by Zotero/Mendeley/etc.
import type { LibraryItem } from "@/lib/library";
import { toCslJson, type CslItem } from "@/lib/citations";

export type CitationExportFormat = "bibtex" | "ris" | "csl-json";

export const CITATION_EXPORT_LABELS: Record<CitationExportFormat, string> = {
  bibtex: "BibTeX (.bib)",
  ris: "RIS (.ris)",
  "csl-json": "CSL-JSON (.json)",
};

export const CITATION_EXPORT_MIME: Record<CitationExportFormat, string> = {
  bibtex: "application/x-bibtex",
  ris: "application/x-research-info-systems",
  "csl-json": "application/vnd.citationstyles.csl+json",
};

export const CITATION_EXPORT_EXTENSION: Record<CitationExportFormat, string> = {
  bibtex: "bib",
  ris: "ris",
  "csl-json": "json",
};

// ---------- shared helpers ----------

const yearOf = (csl: CslItem): string | null => {
  const y = csl.issued?.["date-parts"]?.[0]?.[0];
  return y ? String(y) : null;
};

const authorLastFirst = (
  a: NonNullable<CslItem["author"]>[number],
): string => {
  if (a.literal) return a.literal;
  if (a.family && a.given) return `${a.family}, ${a.given}`;
  return a.family || a.given || "";
};

// ---------- BibTeX ----------

// Escape special characters so the entry round-trips through BibTeX parsers.
const bibtexEscape = (value: string): string =>
  value
    .replace(/\\/g, "\\textbackslash{}")
    .replace(/([&%$#_{}])/g, "\\$1")
    .replace(/~/g, "\\textasciitilde{}")
    .replace(/\^/g, "\\textasciicircum{}");

const BIBTEX_TYPE_BY_CSL: Record<string, string> = {
  book: "book",
  "article-journal": "article",
  chapter: "incollection",
  "paper-conference": "inproceedings",
  webpage: "online",
  report: "techreport",
  broadcast: "misc",
  document: "misc",
};

const slugifyCiteKey = (raw: string): string =>
  raw
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "")
    .slice(0, 40) || "ref";

const bibtexCiteKey = (csl: CslItem, index: number): string => {
  const firstAuthor =
    csl.author?.[0]?.family ||
    csl.author?.[0]?.literal ||
    csl.title ||
    "ref";
  const year = yearOf(csl) ?? "nd";
  const key = `${slugifyCiteKey(firstAuthor)}${year}`;
  // Append index to guarantee uniqueness within a single export.
  return `${key}_${index + 1}`;
};

const formatBibtexEntry = (csl: CslItem, index: number): string => {
  const type = BIBTEX_TYPE_BY_CSL[csl.type] ?? "misc";
  const key = bibtexCiteKey(csl, index);
  const fields: Array<[string, string | undefined]> = [];
  if (csl.author?.length) {
    fields.push([
      "author",
      csl.author.map((a) => authorLastFirst(a)).filter(Boolean).join(" and "),
    ]);
  }
  if (csl.title) fields.push(["title", csl.title]);
  if (csl["container-title"]) {
    const containerField = csl.type === "chapter" ? "booktitle" : "journal";
    fields.push([containerField, csl["container-title"]]);
  }
  if (csl.publisher) fields.push(["publisher", csl.publisher]);
  const year = yearOf(csl);
  if (year) fields.push(["year", year]);
  if (csl.volume) fields.push(["volume", csl.volume]);
  if (csl.edition) fields.push(["edition", csl.edition]);
  if (csl.DOI) fields.push(["doi", csl.DOI]);
  if (csl.URL) fields.push(["url", csl.URL]);
  if (csl.ISBN) fields.push(["isbn", csl.ISBN]);
  if (csl.language) fields.push(["language", csl.language]);

  const body = fields
    .filter(([, v]) => v && v.length > 0)
    .map(([k, v]) => `  ${k} = {${bibtexEscape(String(v))}}`)
    .join(",\n");
  return `@${type}{${key},\n${body}\n}`;
};

export const toBibTeX = (items: LibraryItem[]): string =>
  items.map((it, idx) => formatBibtexEntry(toCslJson(it), idx)).join("\n\n") + "\n";

// ---------- RIS ----------

const RIS_TYPE_BY_CSL: Record<string, string> = {
  book: "BOOK",
  "article-journal": "JOUR",
  chapter: "CHAP",
  "paper-conference": "CPAPER",
  webpage: "ELEC",
  report: "RPRT",
  broadcast: "SOUND",
  document: "GEN",
};

const formatRisEntry = (csl: CslItem): string => {
  const lines: string[] = [];
  lines.push(`TY  - ${RIS_TYPE_BY_CSL[csl.type] ?? "GEN"}`);
  if (csl.author?.length) {
    for (const a of csl.author) {
      const name = authorLastFirst(a);
      if (name) lines.push(`AU  - ${name}`);
    }
  }
  if (csl.title) lines.push(`TI  - ${csl.title}`);
  if (csl["container-title"]) lines.push(`T2  - ${csl["container-title"]}`);
  if (csl.publisher) lines.push(`PB  - ${csl.publisher}`);
  const year = yearOf(csl);
  if (year) lines.push(`PY  - ${year}`);
  if (csl.volume) lines.push(`VL  - ${csl.volume}`);
  if (csl.edition) lines.push(`ET  - ${csl.edition}`);
  if (csl.DOI) lines.push(`DO  - ${csl.DOI}`);
  if (csl.URL) lines.push(`UR  - ${csl.URL}`);
  if (csl.ISBN) lines.push(`SN  - ${csl.ISBN}`);
  if (csl.language) lines.push(`LA  - ${csl.language}`);
  lines.push("ER  - ");
  return lines.join("\n");
};

export const toRIS = (items: LibraryItem[]): string =>
  items.map((it) => formatRisEntry(toCslJson(it))).join("\n\n") + "\n";

// ---------- CSL-JSON ----------

export const toCSLJSON = (items: LibraryItem[]): string =>
  JSON.stringify(items.map((it) => toCslJson(it)), null, 2) + "\n";

// ---------- public API ----------

export const exportBibliography = (
  items: LibraryItem[],
  format: CitationExportFormat,
): string => {
  switch (format) {
    case "bibtex":
      return toBibTeX(items);
    case "ris":
      return toRIS(items);
    case "csl-json":
      return toCSLJSON(items);
    default: {
      const _exhaustive: never = format;
      throw new Error(`Unknown export format: ${_exhaustive as string}`);
    }
  }
};
